import { Component, inject, input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, of } from 'rxjs';
import { SearchService } from '../../../features/search/search.service';
import { SearchFieldFilter, SearchEntityFilter, SearchOptions } from '../../../features/search/search.model';
import { HlmIconComponent } from '../../../ui/icon.component';
import { environment } from '../../../../environments/environment';
interface FilterChip<T> {
  key: T;
  label: string;
  icon?: string;
  active: boolean;
}

@Component({
  selector: 'spike-search-box',
  standalone: true,
  imports: [FormsModule, HlmIconComponent, NgTemplateOutlet],
  template: `
    @if (!compact()) {
      <div class="flex flex-col gap-6 cmt-reveal" style="animation-delay:160ms">
        <div class="flex flex-col gap-1">
          <span class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
            Lookup
          </span>
          <h2 class="font-display text-2xl font-medium italic text-foreground">
            What are we looking for today?
          </h2>
        </div>

        <!-- Hero search input -->
        <div class="relative">
          <hlm-icon
            name="lucideSearch"
            size="1.1rem"
            class="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            class="h-14 w-full rounded-full border border-border bg-card pl-16 pr-36 text-base text-foreground shadow-[0_1px_2px_rgba(20,18,30,0.04),0_8px_24px_-12px_rgba(20,18,30,0.10)] placeholder:text-muted-foreground/70 transition-all focus:border-ring focus:shadow-[0_1px_2px_rgba(20,18,30,0.04),0_16px_40px_-16px_rgba(66,72,166,0.30)] focus:outline-none focus:ring-2 focus:ring-ring/25"
            placeholder="Customer ID, company name, email, extern ID…"
            [(ngModel)]="query"
            (keydown)="onKeydown($event)"
            autocomplete="off"
          />
          @if (query) {
            <button
              type="button"
              (click)="onClear()"
              class="absolute right-[7.5rem] top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Clear"
            >
              <hlm-icon name="lucideX" size="0.95rem" />
            </button>
          }
          <button
            type="button"
            (click)="onSearch()"
            [disabled]="!query.trim()"
            class="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-10 items-center gap-2 rounded-full bg-primary px-5 text-sm font-medium text-primary-foreground shadow-sm transition-all hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
          >
            Search
            <span class="font-mono-feat text-sm opacity-70">↵</span>
          </button>
        </div>

        <p class="text-sm text-muted-foreground/80">
          <span class="font-mono-feat">TIP</span>
          <span class="mx-1.5 text-muted-foreground/40">·</span>
          add <code class="font-mono-feat rounded bg-muted px-1 py-0.5 text-sm">*</code> as a wildcard, e.g. <code class="font-mono-feat rounded bg-muted px-1 py-0.5 text-sm">Mari*</code> matches Maria, Marius…
        </p>

        <!-- Filter chips -->
        <div class="flex flex-col gap-3 rounded-2xl border border-border bg-card/60 p-4">
          <ng-container *ngTemplateOutlet="chipRow; context: { label: 'Search in', chips: fieldChips, toggleAll: toggleAllFields, allOn: allFieldsActive, onToggle: toggleFieldChip }"></ng-container>
          <div class="h-px bg-border/60"></div>
          <ng-container *ngTemplateOutlet="chipRow; context: { label: 'Show', chips: entityChips, toggleAll: toggleAllEntities, allOn: allEntitiesActive, onToggle: toggleEntityChip }"></ng-container>
          @if (brandChips.length) {
            <div class="h-px bg-border/60"></div>
            <ng-container *ngTemplateOutlet="chipRow; context: { label: 'Brand', chips: brandChips, toggleAll: toggleAllBrands, allOn: allBrandsActive, onToggle: toggleBrandChip }"></ng-container>
          }
          @if (hasActiveFilters) {
            <button
              type="button"
              (click)="resetFilters()"
              class="self-start inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <hlm-icon name="lucideRotateCcw" size="0.8rem" />
              Reset filters
            </button>
          }
        </div>
      </div>
    } @else {
      <!-- Compact mode (after search) -->
      <div class="flex items-center gap-3">
        <div class="relative flex-1">
          <hlm-icon
            name="lucideSearch"
            size="0.95rem"
            class="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            class="h-10 w-full rounded-full border border-border bg-card pl-11 pr-10 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/25"
            placeholder="Search…"
            [(ngModel)]="query"
            (keydown)="onKeydown($event)"
          />
          @if (query) {
            <button
              type="button"
              (click)="onClear()"
              class="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear"
            >
              <hlm-icon name="lucideX" size="0.85rem" />
            </button>
          }
        </div>
        <button
          type="button"
          (click)="onSearch()"
          [disabled]="!query.trim()"
          class="inline-flex h-10 items-center gap-2 rounded-full bg-primary px-5 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
        >
          Search
        </button>
        @if (filtersChanged) {
          <button
            type="button"
            (click)="onSearch()"
            class="inline-flex h-10 items-center gap-1.5 rounded-full border border-accent/40 bg-accent/15 px-3 text-sm font-medium text-foreground hover:bg-accent/25"
          >
            <hlm-icon name="lucideRefreshCw" size="0.85rem" />
            Refresh
          </button>
        }
      </div>
    }

    <!-- Reusable chip-row template -->
    <ng-template
      #chipRow
      let-label="label"
      let-chips="chips"
      let-toggleAll="toggleAll"
      let-allOn="allOn"
      let-onToggle="onToggle"
    >
      <div class="flex flex-wrap items-center gap-1.5">
        <span class="font-mono-feat mr-2 text-sm uppercase tracking-[0.2em] text-muted-foreground">
          {{ label }}
        </span>
        @for (chip of chips; track chip.key) {
          <button
            type="button"
            (click)="onToggle(chip)"
            [class]="chipClass(chip.active)"
          >
            @if (chip.icon) {
              <hlm-icon [name]="iconNameFor(chip.icon)" size="0.8rem" />
            }
            {{ chip.label }}
          </button>
        }
        <button
          type="button"
          (click)="toggleAll(!allOn)"
          class="ml-1 font-mono-feat rounded-full px-2 py-0.5 text-sm uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {{ allOn ? 'none' : 'all' }}
        </button>
      </div>
    </ng-template>
  `,
})
export class SpikeSearchBoxComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly searchService = inject(SearchService);

  compact = input(false);
  query = '';
  filtersChanged = false;

  fieldChips: FilterChip<SearchFieldFilter>[] = [
    { key: 'customerId', label: 'Customer ID', active: true },
    { key: 'companyName', label: 'Company', active: true },
    { key: 'name', label: 'Name', active: true },
    { key: 'externId', label: 'Extern ID', active: true },
    { key: 'phoneServiceId', label: 'Phone Service ID', active: true },
    { key: 'userAccountId', label: 'User Account ID', active: true },
  ];

  entityChips: FilterChip<SearchEntityFilter>[] = [
    { key: 'customer', label: 'Customers', icon: 'business', active: true },
    { key: 'phoneservice', label: 'Phone Services', icon: 'phone', active: true },
    { key: 'useraccount', label: 'User Accounts', icon: 'person', active: true },
  ];

  brandChips: FilterChip<string>[] = [];

  ngOnInit(): void {
    this.query = this.searchService.currentQuery;
    this.restoreFilters();
    this.loadBrands();
  }

  private loadBrands(): void {
    this.http.get<{ id: number; name: string }[]>(`${environment.contractUrl}/brands`).pipe(
      catchError(() => of([] as { id: number; name: string }[])),
    ).subscribe(brands => {
      const names = brands.map(b => b.name).sort((a, b) => a.localeCompare(b));
      const saved = this.searchService.currentOptions?.brandFilters;
      this.brandChips = names.map(b => ({
        key: b,
        label: b,
        active: saved?.length ? saved.includes(b) : true,
      }));
    });
  }

  onSearch(): void {
    if (this.query.trim()) {
      this.filtersChanged = false;
      this.searchService.search(this.query, this.buildOptions()).subscribe();
    }
  }

  onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') this.onSearch();
  }

  onClear(): void {
    this.query = '';
    this.filtersChanged = false;
    this.searchService.clearResults();
  }

  toggleFieldChip = (c: FilterChip<SearchFieldFilter>): void => { c.active = !c.active; this.markFiltersChanged(); };
  toggleEntityChip = (c: FilterChip<SearchEntityFilter>): void => { c.active = !c.active; this.markFiltersChanged(); };
  toggleBrandChip = (c: FilterChip<string>): void => { c.active = !c.active; this.markFiltersChanged(); };
  toggleAllFields = (on: boolean): void => { this.fieldChips.forEach(c => c.active = on); this.markFiltersChanged(); };
  toggleAllEntities = (on: boolean): void => { this.entityChips.forEach(c => c.active = on); this.markFiltersChanged(); };
  toggleAllBrands = (on: boolean): void => { this.brandChips.forEach(c => c.active = on); this.markFiltersChanged(); };

  resetFilters(): void {
    this.fieldChips.forEach(c => c.active = true);
    this.entityChips.forEach(c => c.active = true);
    this.brandChips.forEach(c => c.active = true);
    this.markFiltersChanged();
  }

  get hasActiveFilters(): boolean {
    return this.fieldChips.some(c => !c.active) ||
      this.entityChips.some(c => !c.active) ||
      this.brandChips.some(c => !c.active);
  }

  get allBrandsActive(): boolean { return this.brandChips.every(c => c.active); }
  get allFieldsActive(): boolean { return this.fieldChips.every(c => c.active); }
  get allEntitiesActive(): boolean { return this.entityChips.every(c => c.active); }

  chipClass(active: boolean): string {
    return [
      'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition-all',
      active
        ? 'bg-primary/10 text-primary border border-primary/25 hover:bg-primary/15'
        : 'bg-transparent text-muted-foreground/60 border border-border line-through hover:text-muted-foreground hover:border-muted-foreground/30',
    ].join(' ');
  }

  iconNameFor(matName: string): string {
    // Map old Material icon names → Lucide names already registered in HlmIconComponent
    const map: Record<string, string> = {
      business: 'lucideBuilding2',
      phone: 'lucidePhone',
      person: 'lucideUserRound',
    };
    return map[matName] || 'lucideExpand';
  }

  private markFiltersChanged(): void {
    if (this.searchService.currentQuery) this.filtersChanged = true;
  }

  private buildOptions(): SearchOptions {
    const opts: SearchOptions = {};
    const af = this.fieldChips.filter(c => c.active).map(c => c.key);
    const ae = this.entityChips.filter(c => c.active).map(c => c.key);
    if (af.length > 0 && af.length < this.fieldChips.length) opts.fieldFilters = af;
    if (ae.length > 0 && ae.length < this.entityChips.length) opts.entityFilters = ae;
    const ab = this.brandChips.filter(c => c.active).map(c => c.key);
    if (ab.length > 0 && ab.length < this.brandChips.length) opts.brandFilters = ab;
    return opts;
  }

  private restoreFilters(): void {
    const opts = this.searchService.currentOptions;
    if (!opts) return;
    if (opts.fieldFilters?.length) this.fieldChips.forEach(c => c.active = opts.fieldFilters!.includes(c.key));
    if (opts.entityFilters?.length) this.entityChips.forEach(c => c.active = opts.entityFilters!.includes(c.key));
    if (opts.brandFilters?.length) this.brandChips.forEach(c => c.active = opts.brandFilters!.includes(c.key));
  }
}
